import React, { Suspense, lazy } from "react";
import { Navigate, matchRoutes } from "react-router-dom";
import App from "../App";
import {
  APP_ROUTES,
  LEGACY_REDIRECTS,
  NOT_FOUND_PAGE,
  PUBLIC_ROUTES,
} from "./publicRoutes";

// Vite resolves this to a map of lazy importers in both the client and SSR
// builds, so the route table stays a plain list of page names.
const pageModules = import.meta.glob("../pages/*.jsx");

export function loadPage(page) {
  const importer = pageModules[`../pages/${page}.jsx`];
  if (!importer) throw new Error(`Unknown page module: ${page}`);
  return importer();
}

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center" role="status" aria-live="polite">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-emerald-400" aria-hidden="true" />
      <span className="sr-only">Loading page…</span>
    </div>
  );
}

// Auth is loaded lazily so Firebase never enters the marketing page graph.
const RequireAuth = lazy(() => import("../auth/RequireAuth"));

function publicRouteObject(route) {
  return {
    path: route.path === "/" ? undefined : route.path.replace(/^\//, ""),
    index: route.path === "/" ? true : undefined,
    lazy: async () => {
      const mod = await loadPage(route.page);
      return { Component: mod.default };
    },
    handle: { page: route.page, publicRoute: true },
  };
}

function appRouteObject(route) {
  const Page = lazy(() => loadPage(route.page));
  const element = (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  );
  return {
    path: route.path.replace(/^\//, ""),
    element: route.auth ? (
      <Suspense fallback={<PageLoader />}>
        <RequireAuth>{element}</RequireAuth>
      </Suspense>
    ) : (
      element
    ),
    handle: { page: route.page, publicRoute: false },
  };
}

/**
 * Build the shared route tree. `includeAppRoutes` is false on the server so no
 * private route module is ever evaluated at build time.
 */
export function createRouteObjects({ includeAppRoutes = true } = {}) {
  const children = [
    ...PUBLIC_ROUTES.map(publicRouteObject),
    ...LEGACY_REDIRECTS.map(({ from, to }) => ({
      path: from.replace(/^\//, ""),
      element: <Navigate to={to} replace />,
    })),
    ...(includeAppRoutes ? APP_ROUTES.map(appRouteObject) : []),
    {
      path: "*",
      lazy: async () => {
        const mod = await loadPage(NOT_FOUND_PAGE);
        return { Component: mod.default };
      },
      handle: { page: NOT_FOUND_PAGE, publicRoute: true },
    },
  ];

  return [{ path: "/", element: <App />, children }];
}

/**
 * Resolve every lazy route that matches the given location so the initial
 * render is synchronous. Required for hydrateRoot to see the same tree the
 * server rendered instead of a loading fallback.
 */
export async function resolveLazyMatches(routes, pathname) {
  const matches = matchRoutes(routes, pathname) || [];
  await Promise.all(
    matches
      .filter((match) => typeof match.route.lazy === "function")
      .map(async (match) => {
        const resolved = await match.route.lazy();
        Object.assign(match.route, resolved);
        delete match.route.lazy;
      })
  );
  return matches;
}
